import * as Location from 'expo-location';
import { MapLocation } from '../models/MapModels';

const LOCATION_TIMEOUT_MS = 15000;

const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Location request timed out'));
    }, timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeout);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
};

const toMapLocation = (location?: Location.LocationObject | null): MapLocation | undefined => {
  if (!location) {
    return undefined;
  }

  const lat = Number(location.coords?.latitude);
  const lng = Number(location.coords?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return undefined;
  }

  return { lat, lng, isDefault: false };
};

export const getForegroundLocationWithoutPrompt = async (fallbackLocation: MapLocation): Promise<MapLocation> => {
  try {
    const permission = await Location.getForegroundPermissionsAsync();
    if (!permission.granted) {
      return { ...fallbackLocation };
    }

    const lastKnown = toMapLocation(
      await Location.getLastKnownPositionAsync({
        maxAge: 300000,
      }),
    );
    if (lastKnown) {
      return lastKnown;
    }

    const currentLocation = toMapLocation(
      await withTimeout(
        Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
          mayShowUserSettingsDialog: true,
        }),
        LOCATION_TIMEOUT_MS,
      ),
    );
    if (currentLocation) {
      return currentLocation;
    }
  } catch {
    // Fallback behavior is expected when location access is unavailable.
  }

  return { ...fallbackLocation };
};

